import React from 'react';
import { TaskMode } from '../../taskTypes/module';
import type { TaskCommentFieldAnchor } from '../../types';
import { taskCommentFieldAnchorKey } from '../../utils/taskCommentAnchors';
import { TaskShellActions } from './taskShellTypes';
import { describeBlockedFields } from './fieldLabels';

export interface TaskShellFooterProps {
  mode: TaskMode;
  canEditResult?: boolean;
  canClaimReview?: boolean;
  canRequestChanges: boolean;
  submitGate: {
    ok: boolean;
    blockedBy: {
      commentId: string;
      fieldPath: string | null;
      fieldAnchor?: TaskCommentFieldAnchor;
    }[];
  };
  submitValidation: string | null;
  /** Field labels registered by the task view's FieldCommentSlots, keyed by field path. */
  fieldLabels?: ReadonlyMap<string, string>;
  /** Opens the reviewer thread(s) blocking resubmission on a field; `null` is task-level. */
  onFocusBlockedField?: (
    fieldPath: string | null,
    fieldAnchor?: TaskCommentFieldAnchor,
  ) => void;
  busy?: boolean;
  pendingAction?: string | null;
  actions?: TaskShellActions;
  validatorEnabled?: boolean;
  validatorStatus?: 'idle' | 'running' | 'done' | 'error';
  validatorCount?: number;
  validatorError?: string | null;
  workFooter?: React.ReactNode;
}

export function TaskShellFooter({
  mode,
  canEditResult = false,
  canClaimReview,
  canRequestChanges,
  submitGate,
  submitValidation,
  fieldLabels,
  onFocusBlockedField,
  busy,
  pendingAction,
  actions,
  validatorEnabled = false,
  validatorStatus = 'idle',
  validatorCount = 0,
  validatorError = null,
  workFooter = null,
}: TaskShellFooterProps) {
  const isPending = (name: string) => pendingAction === name;
  const anyPending = !!pendingAction;
  // Autosave owns draft persistence; the legacy prop remains accepted by the
  // shell for callers that still provide it, but no Save control is rendered.
  const saveButton = null;

  if (mode === 'preview') {
    const returnToQueueButton = actions?.onReturnToQueue ? (
      <button
        type="button"
        onClick={() => actions.onReturnToQueue!()}
        disabled={busy || anyPending}
        className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
      >
        {isPending('returnToQueue') ? 'Moving…' : 'Move back to queue'}
      </button>
    ) : null;
    const reworkButton = actions?.onRework ? (
      <button
        type="button"
        onClick={() => actions.onRework!()}
        disabled={busy || anyPending}
        className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
      >
        {isPending('rework') ? 'Reopening…' : 'Rework'}
      </button>
    ) : null;
    if (saveButton || reworkButton || returnToQueueButton || canEditResult) {
      return (
        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-gray-200 bg-gray-50 px-5 py-3">
          <span className="mr-auto text-xs text-gray-500">
            {returnToQueueButton
              ? 'Waiting for review — move it back to edit.'
              : reworkButton
                ? 'Read-only — reopen to edit.'
                : 'Preview actions stay disabled, but admin edits can be saved (autosaved).'}
          </span>
          {saveButton}
          {returnToQueueButton}
          {reworkButton}
        </div>
      );
    }
    return (
      <div className="flex items-center justify-end border-t border-gray-200 bg-gray-50 px-5 py-3">
        <span className="text-xs text-gray-500">Preview — actions disabled.</span>
      </div>
    );
  }

  const showValidate = validatorEnabled && !!actions?.onRunValidation;
  const validateBusy = validatorStatus === 'running' || isPending('runValidation');
  const validateDisabled = busy || anyPending || validateBusy;
  const validateLabel = (() => {
    if (validateBusy) return 'Validating…';
    if (validatorStatus === 'error') return 'Validate (retry)';
    if (validatorStatus === 'done') {
      return validatorCount === 0
        ? 'Validate · ✓ no issues'
        : `Validate · ${validatorCount} ${validatorCount === 1 ? 'issue' : 'issues'}`;
    }
    return 'Validate';
  })();
  const validateTitle = (() => {
    if (validatorStatus === 'error' && validatorError) return validatorError;
    if (validatorStatus === 'done') return 'Run validation again to refresh.';
    return 'Run AI checks against the current form. Empty fields are skipped.';
  })();
  const validateButton = showValidate ? (
    <button
      type="button"
      onClick={() => actions!.onRunValidation!()}
      disabled={validateDisabled}
      title={validateTitle}
      className={[
        'rounded-md border px-3 py-1.5 text-xs font-medium disabled:opacity-50',
        validatorStatus === 'error'
          ? 'border-red-300 bg-red-50 text-red-700 hover:bg-red-100'
          : validatorStatus === 'done' && validatorCount > 0
            ? 'border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100'
            : validatorStatus === 'done' && validatorCount === 0
              ? 'border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100'
              : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50',
      ].join(' ')}
    >
      {validateLabel}
    </button>
  ) : null;
  const validateControls = showValidate ? (
    <>
      <span className="text-xs text-gray-500">This can take several minutes.</span>
      {validateButton}
    </>
  ) : null;

  if (mode === 'work') {
    if (workFooter) return <>{workFooter}</>;

    const submitDisabled = busy || anyPending || !submitGate.ok || submitValidation !== null;
    const blockedFields = describeBlockedFields(submitGate.blockedBy, fieldLabels);
    const blockedLabels = blockedFields.map((field) => field.label).join(', ');
    const submitTitle = !submitGate.ok
      ? `Resolve or reply to each reviewer comment to re-submit. Blocked on: ${blockedLabels}`
      : submitValidation ?? undefined;
    // Name the blocking fields in the visible notice and make each one a jump
    // link. A tooltip-only list is easy to miss, and a writer who has replied
    // on four of five threads has no other way to tell which one is still
    // holding the Submit button.
    const inlineMessage = !submitGate.ok ? (
      <>
        Resolve or reply to the reviewer {blockedFields.length === 1 ? 'comment' : 'comments'} on{' '}
        {blockedFields.map((field, index) => (
          <React.Fragment key={taskCommentFieldAnchorKey(field.fieldAnchor) ?? field.fieldPath ?? '__task__'}>
            {index > 0 && (index === blockedFields.length - 1 ? ' and ' : ', ')}
            <button
              type="button"
              onClick={() => {
                if (field.fieldAnchor) onFocusBlockedField?.(field.fieldPath, field.fieldAnchor);
                else onFocusBlockedField?.(field.fieldPath);
              }}
              title="Open this comment thread"
              className="font-semibold underline decoration-amber-400 underline-offset-2 hover:text-amber-900"
            >
              {field.label}
            </button>
          </React.Fragment>
        ))}
        {' '}to re-submit.
      </>
    ) : submitValidation;
    return (
      <div className="flex flex-wrap items-center justify-end gap-2 border-t border-gray-200 bg-gray-50 px-5 py-3">
        {inlineMessage && <span className="mr-auto text-xs text-amber-700">{inlineMessage}</span>}
        {saveButton}
        {validateControls}
        {actions?.onSubmit && (
          <button
            type="button"
            onClick={() => actions.onSubmit!()}
            disabled={submitDisabled}
            title={submitTitle}
            className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPending('submit') ? 'Submitting…' : 'Submit'}
          </button>
        )}
      </div>
    );
  }

  if (canClaimReview) {
    return (
      <div className="flex flex-wrap items-center justify-end gap-2 border-t border-gray-200 bg-gray-50 px-5 py-3">
        {saveButton}
        {actions?.onClaimReview && (
          <button
            type="button"
            onClick={() => actions.onClaimReview!()}
            disabled={busy || anyPending}
            className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-hover disabled:opacity-50"
          >
            {isPending('claimReview') ? 'Claiming…' : 'Claim to review'}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center justify-end gap-2 border-t border-gray-200 bg-gray-50 px-5 py-3">
      {!canRequestChanges && (
        <span className="mr-auto text-xs text-gray-500">
          Nothing to request — leave at least one unresolved comment first.
        </span>
      )}
      {saveButton}
      {validateControls}
      {actions?.onEscalate && (
        <button
          type="button"
          onClick={() => actions.onEscalate!()}
          disabled={busy || anyPending}
          className="rounded-md border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-800 hover:bg-amber-100 disabled:opacity-50"
        >
          {isPending('escalate') ? 'Escalating…' : 'Escalate'}
        </button>
      )}
      {actions?.onRequestChanges && (
        <button
          type="button"
          onClick={() => actions.onRequestChanges!('')}
          disabled={busy || anyPending || !canRequestChanges}
          className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          {isPending('requestChanges') ? 'Requesting…' : 'Request changes'}
        </button>
      )}
      {actions?.onApprove && (
        <button
          type="button"
          onClick={() => actions.onApprove!()}
          disabled={busy || anyPending}
          className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-hover disabled:opacity-50"
        >
          {isPending('approve') ? 'Approving…' : 'Approve'}
        </button>
      )}
    </div>
  );
}
